import { Spokes,SpokesCallId, SpokesContact } from './spokes.js'; 

let ptlControl;

interface ISignalCallbacks {
    doWebHidMute: (instance: number, muted: boolean) => void,
    doWebHidAnswer: (instance: number) => void
    doWebHidHangup: (instance: number) => void
}

export type PtlEventCallbacks = {
    onDeviceSelected?: (deviceName: string, deviceId: string) => void,
}

export async function CreatePtlControl(appId: string, appName : string, signalCallbacks : ISignalCallbacks, eventCallbacks: PtlEventCallbacks)  {
    const ptl = new PtlControl(signalCallbacks, eventCallbacks);
    ptlControl = null;
    ptlControl = await ptl.init(appId, appName);
    return ptlControl;    
}



class PtlControl {
    _signalCallbacks : any = {};
    _eventCallbacks : PtlEventCallbacks = {};
    _appId: string | undefined;
    _appName: string | undefined;
    _deviceList : {deviceId: string, deviceName: string, serialNumber: string} [] = [];
    _spokes: Spokes | undefined;
    _timer: any | undefined;
    _previousPollRate: number = 0;
    _pollRate: number  = 0;
    _selectedDeviceId: string | undefined;
    _deviceDetached: boolean = true;
    _externalCallState = 'idle';
    _externalIsMuted = false;
    _externalCallInstance = 0;
    _instanceStateMap = new Map<number, string>([
        [0, ''],
        [1, ''],
        [2, ''],
        [3, ''], 
    ]);



    constructor (signalCallbacks : any, eventCallbacks : PtlEventCallbacks) {
        this._signalCallbacks = signalCallbacks;
        this._eventCallbacks = eventCallbacks;
    }

    /**
     * Initilialises PtlControl. Will succeed even if Hub not installed or no headsets are found
     * @param appId 
     * @param appName 
     * @returns PtlControl
     */
    async init(appId: string, appName: string) {
        // Connect to the Plantronics REST API
        this._spokes = new Spokes("https://127.0.0.1:32018/Spokes");
        this._deviceList = await this.GetDeviceList();
        this._appId = appId;
        this._appName = appName;
        return this;
    }

    getSpokes() : Spokes {
        if (!this._spokes) throw new Error('PTL control not initialised');
        return this._spokes;
    }

    async GetDeviceList() : Promise<any[]>  {
        const spokes = this.getSpokes();
        let deviceList : {deviceId: string, deviceName: string, serialNumber: string} [] = [];
        const deviceInfoResponse = await spokes.Device.deviceInfoAsync();
        if (!deviceInfoResponse.isError) {
            if (deviceInfoResponse.Result /*[0]*/ != null) {
                // Log Spokes active devices found (first in list returned, index 0)
                if (deviceInfoResponse.Result instanceof Array) {
                    deviceInfoResponse.Result.forEach((d: { Uid: any; ProductName: any; SerialNumber: any; VendorId: any;}) => {
                        if (d.VendorId === 1151) {
                            deviceList.push({
                                deviceId: d.Uid,
                                deviceName: d.ProductName,
                                serialNumber: d.SerialNumber,
                            });
                            console.info("PTL: device found id [" + d.Uid + "], name [" + d.ProductName,", sn [" + d.SerialNumber + "][" + d.VendorId + "]");
                        } else {
                            console.warn("PTL: Non-PTL device ignored id [" + d.Uid + "], name [" + d.ProductName,", sn [" + d.SerialNumber + "][" + d.VendorId + "]");
                        }
                    });
                } else {
                    let d : { Uid: any; ProductName: any; SerialNumber: any; VendorId: any;} = deviceInfoResponse.Result;
                    if (d.VendorId === 1151) {
                        deviceList.push({
                            deviceId: d.Uid,
                            deviceName: d.ProductName,
                            serialNumber: d.SerialNumber});
                        console.info("PTL: device found id [" + d.Uid + "], name [" + d.ProductName,", sn [" + d.SerialNumber + "]");                                        
                    } else {
                        console.warn("PTL: Non-PTL device ignored id [" + d.Uid + "], name [" + d.ProductName,", sn [" + d.SerialNumber + "][" + d.VendorId + "]");
                    }
                }
            } else {
                console.warn("PTL: Error: Device was null on connecting to Spokes. Is there a Plantronics device connected?");
            }
        } else {
            console.warn("PTL: API, no hub found [", deviceInfoResponse.Err?.Error_Code, ", decription [", deviceInfoResponse.Err?.Description,"]");
        }
        return deviceList;
    }

    async SelectByWebHidDevice(deviceId: string, webHidCallState: string, isMuted: boolean, webHidCallInstance: number)  {
        const spokes = this.getSpokes();
        let attachResponse = await spokes.Device.attachAsync(deviceId);
        if (attachResponse.isError || !spokes.Device.isAttached) {
            throw new Error("Failed to attach PTL device [" + deviceId + "]: [" + attachResponse.Err?.Description + "]");
        } 
        console.log("PTL: Attached PTL device [", deviceId, "]");

        const registerResponse = await spokes.Plugin.registerAsync(this._appId);
        if (!registerResponse.isError || registerResponse.Err?.Description === "Plugin exists") {
            // "Plugin exists" -> Erroc_code = 0, Type = 5
            if (registerResponse.Err?.Description === "Plugin exists") {
                console.warn("PTL: Info: registered plugin ", this._appId, ", note: ", registerResponse.Err?.Description, ", unregister plugin elsewhere and repeat registration to fix.");
            } else {
                console.log("PTL: Info: registered plugin ", this._appId, ", note: ", registerResponse.Err?.Description);
            }
        } else {
            throw new Error("PTL: Failed to attach PTL device [" + deviceId + "], registration error [" + registerResponse.Err?.Description + "]");
        }
        this._selectedDeviceId = deviceId;
        this._pollRate = 2000;
        this._deviceDetached = false;
        this._instanceStateMap.set(webHidCallInstance, webHidCallState);
        for (let entry of Array.from(this._instanceStateMap.entries())) {
            if (entry[1] !== '') {
                await this.UpdateHeadsetCallState('', entry[1], entry[0]);
            }
        }
        this.SetCurrentCallInstance();
        await this.UpdateHeadsetMutedState(isMuted);
        this.pollDeviceEventsOrReconnect();
        return;
    }

    private SetCurrentCallInstance() {
        // outgoing call is preferred with a min instance number
        for (const entry of Array.from(this._instanceStateMap.entries())) {
            // the order is important
            if (entry[1] === 'activeOutgoing') {
                if (this._externalCallInstance !== entry[0]) {
                    console.log("PTL: Resetting current call instance from [", this._externalCallInstance, ",", this._externalCallState, "] to [", entry[0],",", entry[1], "]");
            }
                this._externalCallInstance = entry[0];
                this._externalCallState = entry[1];                
                return;
            }
        }
        // incoming call is preferred with a min instance number
        for (const entry of Array.from(this._instanceStateMap.entries())) {
            // the order is important
            if (entry[1] === 'incoming') {
                if (this._externalCallInstance !== entry[0]) {
                    console.log("PTL: Resetting current call instance from [", this._externalCallInstance, ",", this._externalCallState, "] to [", entry[0],",", entry[1], "]");
                }
                this._externalCallInstance = entry[0];
                this._externalCallState = entry[1];                
                return;
            }
        }
        // outgoing call is preferred with a max instance number (consultation call for XOR)
        for (const entry of Array.from(this._instanceStateMap.entries()).reverse()) {
            // the order is important
            if (entry[1] === 'active') {
                if (this._externalCallInstance !== entry[0]) {
                    console.log("PTL: Resetting current call instance from [", this._externalCallInstance, ",", this._externalCallState, "] to [", entry[0],",", entry[1], "]");
                }
                this._externalCallInstance = entry[0];
                this._externalCallState = entry[1];                
                return;
            }
        }

    }

    async Deselect() {
        const spokes = this._spokes;
        if (!spokes) return;
        const selectedDeviceId = this._selectedDeviceId;
        this._selectedDeviceId = undefined;
        this._deviceDetached = true;
        this._pollRate = 0;
        this.pollDeviceEventsOrReconnect();
        if (selectedDeviceId) {
            const unregisterResponse = await spokes.Plugin.unRegisterAsync(this._appId)
            console.log("PTL: Unregister response:", unregisterResponse);
            const releaseResponse = await spokes.Device.releaseAsync()
            if (!releaseResponse || !releaseResponse.isError) {
                console.log("PTL: Released PTL device [", selectedDeviceId, "]");
            } else {
                console.warn("PTL: Error released PTL device [", selectedDeviceId, "]: [",  releaseResponse?.Err?.Description,"]");
            }
        }
    }

    async pollDeviceEventsOrReconnect() {
        let spokes = this._spokes;
        if (!spokes) {
            console.info("PTL: Stopped PTL poll: no spokes");
            clearInterval(this._timer);
            return;
        }
        // supports variable poll rate, 2000ms waiting for a device, 10000ms waiting for Hub to be running
        if (this._previousPollRate !== this._pollRate) {
            clearInterval(this._timer);
            this._previousPollRate = this._pollRate;
            if (this._pollRate > 0) this._timer = setInterval(async () => await this.pollDeviceEventsOrReconnect(), this._pollRate);
            else return;
        }
        if (this._selectedDeviceId && this._deviceDetached) {
            const selectedDeviceId = this._selectedDeviceId;
            console.log("PTL: Reconnecting PTL device [" , selectedDeviceId, "]");
            try {
                await this.SelectByWebHidDevice(selectedDeviceId, this._externalCallState, this._externalIsMuted, this._externalCallInstance);
            } catch (error) {
                console.warn("PTL: Error reconnecting PTL device [", selectedDeviceId, "]");
            }
            return;
        }

        const deviceEvtResponse = await spokes.Device.eventsAsync();
        if (deviceEvtResponse.isError) {
            console.warn("PTL : Error polling for PTL device events: " + deviceEvtResponse.Err?.Description);
            this._deviceDetached = true;
            this._pollRate = 10000;                    
        } else if (deviceEvtResponse.Result.length > 0) {
            for (let i = 0; i < deviceEvtResponse.Result.length; i++) {
                console.log("PTL Device Event: " + deviceEvtResponse.Result[i].Event_Log_Type_Name + ", " + deviceEvtResponse.Result[i].Event_Name);
                if (deviceEvtResponse.Result[i].Event_Log_Type_Name === 'HeadsetButtonPressed' && deviceEvtResponse.Result[i].Event_Name === 'Mute') {
                  // look ahead
                  if (i < deviceEvtResponse.Result.length - 1 && 
                    deviceEvtResponse.Result[i + 1].Event_Log_Type_Name === 'HeadsetStateChange' && 
                    (deviceEvtResponse.Result[i + 1].Event_Name === 'MuteOn' || deviceEvtResponse.Result[i + 1].Event_Name === 'MuteOff')) {
                        try {
                            this._signalCallbacks?.doWebHidMute(undefined, deviceEvtResponse.Result[i + 1].Event_Name === 'MuteOn');
                        } catch (error) {
                            console.error("PTL: failed to notify mute event");
                        }
                    }
                } else if (deviceEvtResponse.Result[i].Event_Log_Type_Name === 'HeadsetButtonPressed' && deviceEvtResponse.Result[i].Event_Name === 'Talk') {
                    try {
                        this._signalCallbacks?.doWebHidTalkButton(this._externalCallInstance);
                    } catch (error) {
                        console.error("PTL: failed to notify talk event");
                    }            
                }
            }
        }

        const callEvtResponse = await spokes.Plugin.callEventsAsync(this._appId);
        if (callEvtResponse.isError) {
            console.warn("PTL: Error polling for call events: " + callEvtResponse.Err?.Description);
            if (callEvtResponse.Err?.Description === "No response.  Server appears to be offline." || callEvtResponse.Err?.Description.includes("Couldn't find plugin")) {
                this._deviceDetached = true;
                this._pollRate = 10000;                    
            }
        } else {
            // display list of events collected from REST service
            if (callEvtResponse.Result.length > 0) {
                for (let i = 0; i < callEvtResponse.Result.length; i++) {
                    console.log("PTL: Call Event: " + callEvtResponse.Result[i]);
                    // // Workout the actual call state and call id in question
                    // var callState = SessionCallState.Lookup[result.Result[i]["Action"]];
                    // var callId = result.Result[i]["CallId"]["Id"];
                    // appendLog("CallState: " + callState + ", Call ID: " + callId);
                }
            }
        }
    }

    async OnExternalCallUpdate(externalCallState : string, externalIsMuted : boolean, externalCallInstance : number) {
        console.log(`PTL: OnExternalCallUpdate(): externalCallState=${externalCallInstance}:${externalCallState} vs this._externalCallState=${this._externalCallInstance}:${this._externalCallState}`);
        if (externalIsMuted !== this._externalIsMuted) { 
            console.log(`PTL: OnExternalCallUpdate(): muted=[${externalIsMuted}]-->[${this._externalIsMuted}]`);
        }

        const oldCallState = this._instanceStateMap.get(externalCallInstance);
        if (!oldCallState || oldCallState !== externalCallState) {
            this._instanceStateMap.set(externalCallInstance, externalCallState);
            await this.UpdateHeadsetCallState(oldCallState, externalCallState, externalCallInstance);
            this.SetCurrentCallInstance();
            console.log(`PTL: OnExternalCallUpdate(): updated state info: lastKnownState=[${oldCallState}] -->[${externalCallState}] for call instance ${externalCallInstance}`);
        }
        if (!oldCallState || externalIsMuted !== this._externalIsMuted) {
            await this.UpdateHeadsetMutedState(externalIsMuted);
        }
    }

    async UpdateHeadsetMutedState(isMuted: boolean) {
        const spokes = this._spokes;
        if (spokes && this._selectedDeviceId) {
            const mutedResponse = await spokes.Plugin.muteCallAsync(this._appId, isMuted);
            if (mutedResponse?.isError) {
                console.warn("PTL: Error when setting PTL muted state:", mutedResponse.Description);
            }
        }
        this._externalIsMuted = isMuted;
        console.log("PTL: Updated PTL muted state to muted", isMuted);
    }

    async UpdateHeadsetCallState(oldCallState: string | undefined, newCallState: string, callInstance: number) {
        const spokes = this._spokes;
        if (spokes && this._selectedDeviceId) {
            switch (newCallState) {
                case 'incoming':
                    const incomingCallResponse = await spokes.Plugin.incomingCallAsync(this._appId, getCallId(callInstance), getContact("Unknown"), "Unknown", "ToHeadset");
                    if (incomingCallResponse?.isError) {
                        console.warn("PTL: Error when setting PTL incoming call:", incomingCallResponse.Description);
                    }
                    break;
                case 'activeOutgoing':
                    const outgoingCallResponse = await spokes.Plugin.outgoingCallAsync(this._appId, getCallId(callInstance), getContact("Unknown"), "ToHeadset");
                    if (outgoingCallResponse?.isError) {
                        console.warn("PTL: Error when setting PTL active call:", outgoingCallResponse.Description);
                    }
                    break;
                case 'active':
                    console.log("PTL: call active, oldCallState=", oldCallState);
                    if (oldCallState === 'incoming') {
                        const answerCallResponse = await spokes.Plugin.answerCallAsync(this._appId, getCallId(callInstance));
                        if (answerCallResponse?.isError) {
                            console.warn("PTL: Error when setting PTL answering call:", answerCallResponse.Description);
                        }
                    } else {
                        console.log("PTL: call active");
                        const insertCallResponse = await spokes.Plugin.insertCallAsync(this._appId, getCallId(callInstance), getContact("Unknown"));
                        if (insertCallResponse?.isError) {
                            console.warn("PTL: Error when inserting PTL active call:", insertCallResponse.Description);
                        }
                    }
                    break;
                case 'idle':
                    const terminateCallResponse = await spokes.Plugin.terminateCallAsync(this._appId, getCallId(callInstance));
                    if (terminateCallResponse?.isError) {
                        console.log("PTL: Error when setting PTL idle call:", terminateCallResponse.Description);
                    }
                    break;        
            }
        }
    }                
}

function getCallId(callid: string | number) {
    return new SpokesCallId({ Id: '' + callid } );
}

function getContact(contactname: string) {
    return new SpokesContact({ Name: contactname });
}





